import { useEffect, useSyncExternalStore } from "react";

// Every form with unsaved edits registers here; the app shell reads the
// registry to block route changes and tab closes (<UnsavedChangesPrompt>).
// Modals register as "modal" so a create flow can leave its own modal behind
// (LEAVE_CLOSING_MODAL) while edits in page forms still prompt.
type Kind = "form" | "modal";
const dirtyForms = new Map<symbol, Kind>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** True while any mounted form has unsaved edits (read at navigation time, never stale). */
export const hasUnsavedChanges = (ignoreModals = false) =>
  [...dirtyForms.values()].some((kind) => !ignoreModals || kind !== "modal");

/** Marks the calling form as holding unsaved edits while `dirty` is true. */
export function useUnsavedChanges(dirty: boolean, kind: Kind = "form") {
  useEffect(() => {
    if (!dirty) return;
    const id = Symbol("dirty-form");
    dirtyForms.set(id, kind);
    notify();
    return () => { dirtyForms.delete(id); notify(); };
  }, [dirty, kind]);
}

/** Re-renders when the registry flips between clean and dirty. */
export function useHasUnsavedChanges() {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    hasUnsavedChanges,
  );
}

/**
 * Navigation options for a move the user already chose after deleting
 * something (edits to what was deleted are moot): `navigate(to, LEAVE_WITHOUT_PROMPT)`.
 */
export const LEAVE_WITHOUT_PROMPT = { state: { leaveWithoutPrompt: true } } as const;

/**
 * Navigation options for leaving a create modal that just succeeded: the
 * modal's own input is saved, but unsaved page forms still prompt.
 */
export const LEAVE_CLOSING_MODAL = { state: { leaveWithoutPrompt: "modal" } } as const;

/** What a navigation skips: everything, only open modals, or nothing. */
export function leavesWithoutPrompt(state: unknown): "all" | "modal" | null {
  const v = typeof state === "object" && state !== null ? (state as Record<string, unknown>).leaveWithoutPrompt : undefined;
  return v === true ? "all" : v === "modal" ? "modal" : null;
}
